import type { AuthChangeEvent } from '@supabase/auth-js';
import type { AdminUser } from '../types.ts';

// When an auth provider should re-read "who am I" from the API.
//
// The admin and ops providers called their /me endpoint on EVERY
// onAuthStateChange event. supabase-js fires far more of those than there are
// changes of user: INITIAL_SESSION on subscribe (the provider's own getSession
// had already loaded), TOKEN_REFRESHED roughly hourly, and SIGNED_IN on every
// tab refocus — auth-js's visibility recovery re-announces the stored session
// under that name. So an admin load cost two /me calls, every refocus another
// (~330ms each, and a fresh loading flicker), and each one re-derived the
// sidebar's team region from scratch.
//
// The rule now: the API's answer depends on WHO is signed in, so it is
// re-read only when that changes. Everything else just carries the new token.

export type AuthEventAction = 'ignore' | 'token-only' | 'load-user';

/**
 * What an onAuthStateChange event means for the provider.
 *
 * - `ignore`: INITIAL_SESSION — the provider's own getSession() owns the
 *   restore, and loading again here was the second /me of every page load.
 * - `token-only`: same auth user as the one already loaded (or loading) —
 *   TOKEN_REFRESHED, refocus SIGNED_IN, USER_UPDATED: keep the session and the
 *   API token current, nothing else.
 * - `load-user`: a different user, a sign-out (null), or no successful load
 *   yet — read the identity again.
 */
export function authEventAction(
  event: AuthChangeEvent,
  nextAuthUserId: string | null,
  loadedFor: string | null | undefined,
): AuthEventAction {
  if (event === 'INITIAL_SESSION') return 'ignore';
  return nextAuthUserId === loadedFor ? 'token-only' : 'load-user';
}

/**
 * One identity load per auth user, shared by every caller that asks for it.
 *
 * A sign-in reaches the provider twice — the SIGNED_IN event (fired inside
 * signInWithPassword, before it returns) and the sign-in function's own await —
 * and both must wait on ONE request. `run` returns the in-flight (or finished)
 * load for the same user instead of starting another; `force` (an explicit
 * reload) always starts a fresh one.
 *
 * A failed load is forgotten, so the next event for that user — a token
 * refresh after a 401, say — tries again instead of being deduplicated into a
 * permanent failure. `isCurrent` lets a load that was overtaken (the user
 * changed while it was in flight) drop its answer instead of overwriting the
 * newer one.
 */
export type SessionUserLoader = {
  /** The auth user id of the load in flight or last succeeded; undefined if none. */
  loadedFor(): string | null | undefined;
  run(
    authUserId: string | null,
    task: (isCurrent: () => boolean) => Promise<boolean>,
    opts?: { force?: boolean },
  ): Promise<void>;
};

export function createSessionUserLoader(): SessionUserLoader {
  let current: { authUserId: string | null; promise: Promise<void> } | null = null;
  return {
    loadedFor: () => (current ? current.authUserId : undefined),
    run(authUserId, task, opts) {
      if (!opts?.force && current && current.authUserId === authUserId) return current.promise;
      const entry: { authUserId: string | null; promise: Promise<void> } = {
        authUserId,
        promise: Promise.resolve(),
      };
      const isCurrent = () => current === entry;
      entry.promise = task(isCurrent).then(
        (ok) => {
          if (!ok && isCurrent()) current = null;
        },
        () => {
          if (isCurrent()) current = null;
        },
      );
      current = entry;
      return entry.promise;
    },
  };
}

/**
 * Whether signing in can change the brand GET /api/settings answers on this
 * origin — i.e. whether the portal must re-fetch it with the bearer.
 *
 * Not on a tenant's own per-audience host (`audience` non-null). There the
 * token-less load already resolved the tenant: the API ranks the request's
 * hostname first and the browser's Origin last (apps/api/src/routes/
 * settings.ts), and both name this tenant — so the bearer-resolved answer is
 * the same brand, fetched again on every restore, sign-in and hourly token
 * refresh. Only a host that names no tenant (the legacy shared base, local
 * dev) needs the token to find one.
 */
export function brandingFollowsSession(hostAudience: string | null): boolean {
  return hostAudience === null;
}

/**
 * The sidebar team region to restore at sign-in from the persisted id.
 *
 * Restricted admins may select any region in their SCOPE — the assigned
 * regions plus every descendant. Checked against only the directly assigned
 * ids (all an older /me reported), a persisted sub-region selection was reset
 * to "All" on every load — and, while /me re-ran on every refocus and token
 * refresh, every few minutes mid-session too. An API without
 * `team_region_scope` still gets that older, stricter answer rather than an
 * unchecked one. Unrestricted admins accept any stored id; the shell
 * reconciles a deleted region back to "All" once the live list loads.
 */
export function restoredTeamRegion(stored: string | null, user: AdminUser): string | null {
  if (!stored) return null;
  const assigned = user.team_regions.map((r) => r.id);
  if (assigned.length === 0) return stored;
  const scope = user.team_region_scope ?? assigned;
  return scope.includes(stored) ? stored : null;
}
